import { Component, type ErrorInfo, type ReactNode } from "react";

interface Props {
  children: ReactNode;
  label?: string;
}

interface State {
  error: Error | null;
}

/**
 * Error boundary scoped to a single dashboard section. If one section throws
 * (e.g. Recharts choking on partial data mid-stream), only that section
 * degrades to an inline notice — the answer, KPIs, chips, and other sections
 * keep working.
 *
 * React 18 still requires a class component for error boundaries.
 */
export class SectionErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.warn("[dashboard-agent] section render failed:", error, info.componentStack);
  }

  render() {
    if (this.state.error) {
      return (
        <div className="rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-3 text-xs text-destructive/90">
          <div className="font-medium mb-1">
            Couldn't render this part of the dashboard{this.props.label ? ` (${this.props.label})` : ""}
          </div>
          <div className="text-destructive/70">{this.state.error.message}</div>
        </div>
      );
    }
    return this.props.children;
  }
}
